import { InputNumber, Spin } from "antd";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import api from "../../../services/api";

interface PositionInputProps {
  queryParamValue?: string | undefined;
  value?: number | undefined;
  onChange?: (value: number | undefined) => void;
  endpoint: string;
  disabled?: boolean;
  queryParamName?: string;
  entityId?: string;
}

export function PositionInput({
  queryParamValue,
  onChange,
  endpoint,
  disabled = false,
  queryParamName = "categoryId",
  entityId,
}: PositionInputProps) {
  const [localValue, setLocalValue] = useState<number | null>(null);

  const queryParams: any = {};
  if (queryParamValue && queryParamName) {
    queryParams[queryParamName] = queryParamValue;
  }

  const { data: count, isLoading } = useQuery({
    queryKey: [`${endpoint}/count`, queryParamValue, queryParamName],
    queryFn: async () => {
      if (!queryParamValue) {
        return 0;
      }
      const response = await api.get<{ count: number }>(`${endpoint}/count`, {
        params: queryParams,
      });
      return response.count;
    },
    enabled: !!queryParamValue,
  });

  const { data: defaultPosition, isLoading: isLoadingDefaultPosition } =
    useQuery({
      queryKey: [`${endpoint}/default-position`, entityId],
      queryFn: async () => {
        const response = await api.get<{ position: number }>(
          `${endpoint}/${entityId}/default-position`
        );
        return response.position;
      },
      enabled: !!entityId,
    });

  const [defaultValueSet, setDefaultValueSet] = useState(false);

  useEffect(() => {
    setDefaultValueSet(false);
    setLocalValue(null);
  }, [entityId]);

  useEffect(() => {
    if (isLoadingDefaultPosition || isLoading) {
      return;
    }
    if (defaultValueSet && localValue !== null) {
      return;
    }

    if (defaultPosition !== undefined && defaultPosition !== null && !defaultValueSet) {
      setLocalValue(defaultPosition);
      onChange?.(defaultPosition);
      setDefaultValueSet(true);
      return;
    }

    if (count !== undefined && count !== null && !defaultValueSet && !defaultPosition) {
      const newPosition = 1;
      setLocalValue(newPosition);
      onChange?.(newPosition);
      setDefaultValueSet(true);
      return;
    }

    if (!isLoadingDefaultPosition && !defaultValueSet) {
      setLocalValue(null);
    }
  }, [
    count,
    onChange,
    defaultValueSet,
    localValue,
    defaultPosition,
    isLoading,
    isLoadingDefaultPosition,
  ]);

  const handleChange = (val: number | null) => {
    setLocalValue(val);
    if (val === null || val === undefined) {
      onChange?.(undefined);
      return;
    }

    const maxValue = (count || 1);
    const validValue = Math.max(1, Math.min(val, maxValue || 1));
    setLocalValue(validValue);
    onChange?.(validValue);
  };

  const maxPosition = (count || 1);
  const isDisabled = disabled || isLoading || isLoadingDefaultPosition;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Spin spinning={isLoading} size="small">
        <InputNumber
          min={1}
          max={maxPosition}
          value={localValue}
          onChange={handleChange}
          disabled={isDisabled}
          placeholder="Enter position"
          style={{ width: 120 }}
        />
      </Spin>
      {maxPosition > 0 && !isLoading && (
        <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
          из {maxPosition} (максимальная позиция)
        </span>
      )}
    </div>
  );
}

export default PositionInput;
